// Phones: quick capture into Spark, browsing and commenting on boards, and a read-only Map with your
// location when you allow it. No canvas editing (spec §8).
import { useEffect, useRef, useState } from 'react';
import { SHARED_BOARDS, boardMap } from '../../shared/doc.js';
import { boardReadingOrder } from '../../shared/ops.js';
import type { Project } from '../api.js';
import { CardBody } from '../cards/CardBody.js';
import { CardPanel } from '../cards/CardPanel.js';
import { useToasts } from '../components.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';
import { MapView } from '../map/MapView.js';
import { href, navigate, type ProjectView } from '../router.js';
import { SparkList } from '../spark/SparkList.js';

type Tab = 'spark' | 'research' | 'moodboard' | 'map';
const TABS: { tab: Tab; label: string }[] = [
  { tab: 'spark', label: 'Spark' },
  { tab: 'research', label: 'Research' },
  { tab: 'moodboard', label: 'Moodboard' },
  { tab: 'map', label: 'Map' },
];

function BoardList({ conn, projectId, boardId, onOpen }: { conn: ProjectConn; projectId: string; boardId: string; onOpen: (id: string) => void }) {
  useYVersion(boardMap(conn.doc, boardId));
  const ids = boardReadingOrder(conn.doc, boardId);
  return (
    <ol className="phone-board">
      {ids.map((id) => (
        <li key={id} data-card-id={id} onClick={() => onOpen(id)}>
          <CardBody conn={conn} projectId={projectId} cardId={id} editing={false} onDoneEditing={() => {}} />
        </li>
      ))}
      {ids.length === 0 && <li className="muted">This board is empty.</li>}
    </ol>
  );
}

/** The tab a phone route maps to, falling back to Spark for a view phones don't have their own tab for. */
const tabFor = (view: ProjectView): Tab => (view === 'spark' || view === 'research' || view === 'moodboard' || view === 'map' ? view : 'spark');

export function PhoneProject({ conn, project, banner, view, focus = null }: { conn: ProjectConn; project: Project; banner: React.ReactNode; view: ProjectView; focus?: string | null }) {
  // A fresh project always opens on this view (spec §8: phones are for quick capture first), even
  // though its URL says "research" (the same link desktop uses, where research/the canvas is the
  // right default): phones ignore that and start on Spark unless the link was already pointed at
  // the Map (e.g. a saved "Show on map" link opened straight on a phone). This is a one-time,
  // first-mount-only default — every later route change is followed exactly, below.
  const [tab, setTab] = useState<Tab>(view === 'map' ? 'map' : 'spark');
  const [open, setOpen] = useState<string | null>(null);
  const { toast, view: toasts } = useToasts();
  const mounted = useRef(false);
  // Follows every later route change onto its matching tab — a "Show on map" link, the browser's
  // own Back/Forward (fix round 2: the first version of this effect only ever moved the tab *to*
  // Map, so Back from Map to Research left the hash saying "research" while the Map tab stayed
  // shown), or anything else that changes the URL. The very first run, right after mount, is
  // skipped: the initial tab above already applied the Spark-default rule for it, and re-running
  // the plain `view → tab` mapping here would instead flip a fresh "research"-linked project
  // straight to the Research tab.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setTab(tabFor(view));
  }, [view, focus]);
  const goTo = (t: Tab) => {
    setTab(t);
    navigate(href.project(project.id, t));
  };
  return (
    <main className="phone">
      <header className="topbar">
        <a href={href.projects()} className="back" aria-label="All projects">←</a>
        <h1>{project.title}</h1>
      </header>
      {banner}
      <nav className="tabs">
        {TABS.map((t) => (
          <button key={t.tab} aria-current={tab === t.tab ? 'page' : undefined} onClick={() => goTo(t.tab)}>
            {t.label}
          </button>
        ))}
      </nav>
      {!conn.synced ? (
        <p className="loading">Connecting…</p>
      ) : tab === 'spark' ? (
        <SparkList conn={conn} projectId={project.id} onOpenCard={setOpen} toast={toast} />
      ) : tab === 'map' ? (
        <MapView conn={conn} projectId={project.id} onOpenCard={setOpen} toast={toast} readOnly focusPlaceId={focus} />
      ) : (
        <BoardList conn={conn} projectId={project.id} boardId={tab === 'research' ? SHARED_BOARDS.research : SHARED_BOARDS.moodboard} onOpen={setOpen} />
      )}
      {open && <CardPanel conn={conn} projectId={project.id} cardId={open} readOnly onOpenCard={setOpen} onClose={() => setOpen(null)} />}
      {toasts}
    </main>
  );
}
